const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nexa-backend-red.vercel.app';

export type ViewType = 'website' | 'blog' | 'portfolio' | 'home' | 'about' | 'contact' | 'gallery' | 'team';

export interface TrackViewRequest {
  viewType: ViewType;
  resourceId?: string;
}

export interface TrackViewResponse {
  message: string;
  data: {
    id: string;
    viewType: ViewType;
    resourceId: string | null;
    viewedAt: string;
  };
}

export interface View {
  _id: string;
  viewType: ViewType;
  resourceId: string | null;
  ipAddress: string;
  userAgent: string;
  referer: string | null;
  viewedAt: string;
}

export interface ViewsResponse {
  data: View[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface ViewStats {
  totalViews: number;
  uniqueVisitors: number;
  viewsByType: Array<{
    viewType: ViewType;
    count: number;
  }>;
  topResources: Array<{
    resourceId: string;
    viewType: ViewType;
    count: number;
  }>;
  viewsOverTime?: Array<{
    date: string;
    count: number;
  }>;
}

export interface BlogViewsResponse {
  id: string;
  views: number;
}

// Track a view
export const trackView = async (viewType: ViewType, resourceId?: string): Promise<TrackViewResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/views`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ viewType, resourceId }),
    });

    if (!response.ok) {
      throw new Error(`Failed to track view: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error tracking view:', error);
    throw error;
  }
};

// Get all views with optional filters
export const getViews = async (params?: {
  viewType?: ViewType;
  resourceId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  page?: number;
}): Promise<ViewsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.viewType) queryParams.append('viewType', params.viewType);
    if (params?.resourceId) queryParams.append('resourceId', params.resourceId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());

    const url = `${API_BASE_URL}/api/views${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch views: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching views:', error);
    throw error;
  }
};

// Get view statistics
export const getViewStats = async (params?: {
  startDate?: string;
  endDate?: string;
}): Promise<ViewStats> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const url = `${API_BASE_URL}/api/views/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch stats: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching stats:', error);
    throw error;
  }
};

// Get views count for a specific blog post
export const getBlogViews = async (blogId: string): Promise<BlogViewsResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/blog/${blogId}/views`);

    if (!response.ok) {
      throw new Error(`Failed to fetch blog views: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching blog views:', error);
    throw error;
  }
};

